/**
 * DailyDigest — Morning briefing with proactive intelligence.
 *
 * Aggregates:
 * - Today's meetings with prep status
 * - Urgent actions (overdue, declining accounts)
 * - Commitments due
 * - Top priority questions
 * - Territory health summary
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Sun, Sunrise, Moon, Calendar, AlertTriangle,
  Clock, ChevronRight, Target,
  Mail, Phone, DollarSign, Loader2, RefreshCw,
  Building2, FileText,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import MeetingPrep from '../MeetingPrep/MeetingPrep';
import StatLine from '../shared/StatLine';
import './DailyDigest.css';

const SEVERITY_RANK = { critical: 3, high: 2, medium: 1, low: 0 };

// The digest generates one question row per account, so the same question
// arrives N times with N CRITICAL badges (audit ledger #12). Group by
// question text: one entry, the accounts listed under it, ONE severity
// badge (the highest).
function groupQuestions(questions) {
  const byText = new Map();
  (questions || []).forEach((q) => {
    const key = q.question_text;
    if (!byText.has(key)) {
      byText.set(key, { id: q.id, text: key, severity: q.severity, accounts: [] });
    }
    const g = byText.get(key);
    if (q.account_name && !g.accounts.includes(q.account_name)) g.accounts.push(q.account_name);
    if ((SEVERITY_RANK[q.severity] ?? 0) > (SEVERITY_RANK[g.severity] ?? 0)) g.severity = q.severity;
  });
  return [...byText.values()];
}

function formatMoney(value) {
  const n = Number(value) || 0;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function getGreetingIcon() {
  const hour = new Date().getHours();
  if (hour < 12) return Sunrise;
  if (hour < 17) return Sun;
  return Moon;
}

function DailyDigest() {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState(null);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['daily-digest'],
    queryFn: () => apiClient.get('/api/account-intelligence/digest'),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const digest = data?.digest;
  const GreetingIcon = getGreetingIcon();

  if (isLoading) {
    return (
      <div className="dd-container">
        <div className="dd-loading">
          <Loader2 size={24} className="dd-spinner" />
          <span>Preparing your daily brief...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dd-container">
        <div className="dd-error">
          <AlertTriangle size={20} />
          <span>Failed to load digest</span>
          <button onClick={() => refetch()}>
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      </div>
    );
  }

  if (!digest) return null;

  const { summary, attention_items, meetings, commitments, questions, accounts, territory } = digest;

  return (
    <div className="dd-container">
      {/* Header */}
      <header className="dd-header">
        <div className="dd-greeting">
          <GreetingIcon size={24} />
          <div>
            <h1>{digest.greeting}</h1>
            <p className="dd-date">{formatDate(digest.date)}</p>
          </div>
        </div>
        <button
          className="dd-refresh"
          onClick={() => refetch()}
          disabled={isFetching}
          title="Refresh digest"
        >
          <RefreshCw size={16} className={isFetching ? 'dd-spinner' : ''} />
        </button>
      </header>

      {/* At a glance — inline stat line, not four boxed tiles (audit §5).
          Zeros stay visible: "0 overdue" is the good news. */}
      <StatLine
        stats={[
          { value: summary.meetings_today, label: 'meetings' },
          { value: summary.actions_due, label: 'due today' },
          { value: summary.overdue_items, label: 'overdue' },
          { value: summary.accounts_at_risk, label: 'at risk' },
        ]}
      />

      {/* Attention Needed */}
      {attention_items?.length > 0 && (
        <section className="dd-section">
          <h2 className="dd-section-title">Attention Needed</h2>
          <div className="dd-attention-list">
            {attention_items.map((item, i) => (
              <div key={i} className={`dd-attention-item dd-priority-${item.priority}`}>
                <div className="dd-attention-main">
                  <span className="dd-attention-title">{item.title}</span>
                  <span className="dd-attention-subtitle">{item.subtitle}</span>
                </div>
                <ChevronRight size={14} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Today's Meetings */}
      {meetings?.length > 0 && (
        <section className="dd-section">
          <h2 className="dd-section-title">Today's Meetings</h2>
          <div className="dd-meetings-list">
            {meetings.map((m) => (
              <div
                key={m.meeting_id}
                className={`dd-meeting ${m.needs_prep ? 'dd-meeting-clickable' : ''}`}
                onClick={() => setSelectedMeetingId(m.meeting_id)}
                role="button"
                tabIndex={0}
              >
                <div className="dd-meeting-time">{m.time}</div>
                <div className="dd-meeting-info">
                  <span className="dd-meeting-title">{m.title}</span>
                  {m.department_label && (
                    <span className="dd-meeting-account">{m.department_label}</span>
                  )}
                </div>
                {m.needs_prep ? (
                  <span className="dd-meeting-prep" title="Click to view prep">
                    <FileText size={12} /> Prep
                  </span>
                ) : (
                  <ChevronRight size={14} className="dd-meeting-chevron" />
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Commitments */}
      {(commitments.overdue?.length > 0 || commitments.due_today?.length > 0) && (
        <section className="dd-section">
          <h2 className="dd-section-title">Commitments</h2>
          <div className="dd-commitments">
            {commitments.overdue?.length > 0 && (
              <div className="dd-commitment-group">
                <h3 className="dd-commitment-label dd-overdue">Overdue</h3>
                {commitments.overdue.map((c) => (
                  <div key={c.id} className="dd-commitment dd-commitment-overdue">
                    <span className="dd-commitment-desc">{c.description}</span>
                    <span className="dd-commitment-account">{c.account_name || ''}</span>
                  </div>
                ))}
              </div>
            )}
            {commitments.due_today?.length > 0 && (
              <div className="dd-commitment-group">
                <h3 className="dd-commitment-label">Due Today</h3>
                {commitments.due_today.map((c) => (
                  <div key={c.id} className="dd-commitment">
                    <span className="dd-commitment-desc">{c.description}</span>
                    <span className="dd-commitment-account">{c.account_name || ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Questions to Consider — deduped: one entry per distinct question,
          accounts grouped beneath it, one severity badge (the highest). */}
      {questions?.length > 0 && (
        <section className="dd-section">
          <h2 className="dd-section-title">Questions to Consider</h2>
          <div className="dd-questions">
            {groupQuestions(questions).map((g) => (
              <div key={g.id} className="dd-question">
                <p className="dd-question-text">{g.text}</p>
                <div className="dd-question-meta">
                  <span className="dd-question-account">
                    {g.accounts.slice(0, 4).join(' · ')}
                    {g.accounts.length > 4 && ` · +${g.accounts.length - 4} more`}
                  </span>
                  <span className={`dd-question-severity dd-severity-${g.severity}`}>
                    {g.severity}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Account Alerts */}
      {accounts.declining?.length > 0 && (
        <section className="dd-section">
          <h2 className="dd-section-title">
            Accounts Declining
          </h2>
          <div className="dd-accounts">
            {accounts.declining.map((a) => (
              <div
                key={a.account_id}
                className="dd-account"
                onClick={() => navigate('/workspace')}
              >
                <Building2 size={14} />
                <span className="dd-account-name">{a.account_name}</span>
                <span className="dd-account-score">{a.overall_health}</span>
                <span className="dd-account-trend">
                  {a.health_trend < 0 ? '' : '+'}{a.health_trend}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Territory Summary */}
      <section className="dd-section dd-territory">
        <h2 className="dd-section-title">Territory Health</h2>
        <div className="dd-territory-grid">
          <div className="dd-territory-card">
            <h4>Last 7 Days</h4>
            <div className="dd-territory-stats">
              <div className="dd-stat">
                <Calendar size={12} />
                <span>{territory.activity_7d.meetings} meetings</span>
              </div>
              <div className="dd-stat">
                <Mail size={12} />
                <span>{territory.activity_7d.emails} emails</span>
              </div>
              <div className="dd-stat">
                <Phone size={12} />
                <span>{territory.activity_7d.calls} calls</span>
              </div>
            </div>
          </div>
          <div className="dd-territory-card">
            <h4>Pipeline</h4>
            <div className="dd-territory-stats">
              <div className="dd-stat">
                <DollarSign size={12} />
                <span>{formatMoney(territory.pipeline.total_value)}</span>
              </div>
              <div className="dd-stat">
                <Target size={12} />
                <span>{territory.pipeline.total_opportunities} opportunities</span>
              </div>
              <div className="dd-stat">
                <Clock size={12} />
                <span>{territory.pipeline.closing_soon} closing soon</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="dd-footer">
        <p>Generated at {new Date(digest.generated_at).toLocaleTimeString()}</p>
      </footer>

      {/* Meeting Prep Modal */}
      {selectedMeetingId && (
        <div className="dd-prep-overlay" onClick={() => setSelectedMeetingId(null)}>
          <div className="dd-prep-panel" onClick={(e) => e.stopPropagation()}>
            <MeetingPrep
              meetingId={selectedMeetingId}
              onClose={() => setSelectedMeetingId(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default DailyDigest;
